import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Folder, MapPin, Shield, FileWarning, Search,
  Globe, Smartphone, Upload, Archive, Trash2, ArrowUpDown,
  ChevronUp, ChevronDown,
} from 'lucide-react';

import useProjectStore from '../stores/projectStore';
import { Project, NewProjectForm, ProjectSource, ProjectSortField, StructuredAddress } from '../types';
import { cn, getProjectDisplayName, formatDate } from '../lib/utils';

import { Button } from '../components/ui/Button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/Card';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { ViewToggle } from '../components/scope/ViewToggle';
import AddressAutocomplete from '../components/AddressAutocomplete';
import { DAMAGE_TYPES, CLAIM_TYPES } from '../data/roomChecklists';

// ================================================================= //
//          US States for dropdown                                     //
// ================================================================= //

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
];

// ================================================================= //
//          Source Badge Component                                      //
// ================================================================= //

/**
 * Displays a pill badge indicating the project source (web, mobile, uploaded).
 *
 * Args:
 *   source (ProjectSource | undefined): The project source to display.
 */
const SourceBadge: React.FC<{ source?: ProjectSource | string }> = ({ source }) => {
  if (!source) return null;

  const config: Record<string, { icon: React.ReactNode; label: string; className: string }> = {
    web: {
      icon: <Globe className="mr-1 h-3 w-3" />,
      label: 'Web',
      className: 'bg-blue-50 text-blue-700',
    },
    mobile: {
      icon: <Smartphone className="mr-1 h-3 w-3" />,
      label: 'Mobile',
      className: 'bg-purple-50 text-purple-700',
    },
    uploaded: {
      icon: <Upload className="mr-1 h-3 w-3" />,
      label: 'Uploaded',
      className: 'bg-amber-50 text-amber-700',
    },
  };

  const cfg = config[source] || config.web;

  return (
    <span className={cn(
      'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
      cfg.className
    )}>
      {cfg.icon}
      {cfg.label}
    </span>
  );
};

// ================================================================= //
//          Project Card Component                                     //
// ================================================================= //

interface ProjectCardEnhancedProps {
  project: Project;
  onArchive: (id: string) => void;
  onDelete: (id: string) => void;
}

/**
 * Enhanced project card with source badge and archive/delete actions.
 */
const ProjectCard: React.FC<ProjectCardEnhancedProps> = ({ project, onArchive, onDelete }) => {
  const navigate = useNavigate();
  const displayName = getProjectDisplayName(project);
  const location = [project.city, project.state, project.zip].filter(Boolean).join(', ');
  const projectSource = project.metadata?.source as ProjectSource | undefined;

  return (
    <Card
      className={cn(
        'flex cursor-pointer flex-col shadow-sm transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5',
        project.ahj ? 'border-l-4 border-l-emerald-500' : 'border-l-4 border-l-slate-200'
      )}
      onClick={() => navigate(`/project/${project.id}`)}
      role="link"
      aria-label={`View project for ${displayName}`}
    >
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base truncate">{displayName}</CardTitle>
          <SourceBadge source={projectSource} />
        </div>
        {project.address && (
          <CardDescription className="flex items-center text-sm">
            <MapPin className="mr-1.5 h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate">{project.address}</span>
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="flex-grow pb-2">
        {location && (
          <p className="text-xs text-muted-foreground">{location}</p>
        )}
      </CardContent>
      <CardFooter className="flex flex-wrap items-center gap-2 pt-0">
        {project.claim_number && (
          <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
            <FileWarning className="mr-1 h-3 w-3" />
            {project.claim_number}
          </span>
        )}
        {project.ahj && (
          <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700">
            <Shield className="mr-1 h-3 w-3" />
            AHJ
          </span>
        )}
        {project.date_of_loss && (
          <span className="text-xs text-muted-foreground">
            Loss: {new Date(project.date_of_loss).toLocaleDateString()}
          </span>
        )}
        {/* Spacer to push actions right */}
        <div className="flex-grow" />
        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => onArchive(project.id)}
            className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
            title="Archive project"
          >
            <Archive className="h-3.5 w-3.5" />
          </button>
          <button
            onClick={() => onDelete(project.id)}
            className="rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-600"
            title="Delete project"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </CardFooter>
    </Card>
  );
};

// ================================================================= //
//          Project Table View Component                                //
// ================================================================= //

interface ProjectTableViewProps {
  projects: Project[];
  sortField: ProjectSortField;
  sortOrder: 'asc' | 'desc';
  onSort: (field: ProjectSortField) => void;
  onArchive: (id: string) => void;
  onDelete: (id: string) => void;
}

/**
 * Table view for projects with sortable columns and action buttons.
 */
const ProjectTableView: React.FC<ProjectTableViewProps> = ({
  projects, sortField, sortOrder, onSort, onArchive, onDelete,
}) => {
  const navigate = useNavigate();

  /**
   * Renders sort indicator arrow for a column header.
   *
   * Args:
   *   field (ProjectSortField): The field this column sorts by.
   *
   * Returns:
   *   React.ReactNode: Sort indicator icon.
   */
  const renderSortIcon = (field: ProjectSortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-3 w-3 opacity-30" />;
    }
    return sortOrder === 'asc'
      ? <ChevronUp className="ml-1 h-3 w-3" />
      : <ChevronDown className="ml-1 h-3 w-3" />;
  };

  const headerClass = 'px-4 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer hover:text-slate-700 select-none';

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse">
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50/50">
            <th className={headerClass} onClick={() => onSort('name')}>
              <span className="inline-flex items-center">Address {renderSortIcon('name')}</span>
            </th>
            <th className={headerClass} onClick={() => onSort('date')}>
              <span className="inline-flex items-center">Date {renderSortIcon('date')}</span>
            </th>
            <th className={headerClass} onClick={() => onSort('status')}>
              <span className="inline-flex items-center">Status {renderSortIcon('status')}</span>
            </th>
            <th className={cn(headerClass, 'cursor-default hover:text-slate-500')}>Source</th>
            <th className={cn(headerClass, 'cursor-default hover:text-slate-500 text-right')}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((project) => {
            const projectSource = project.metadata?.source as ProjectSource | undefined;
            const displayDate = project.date_of_loss || project.created_at;
            const status = project.status || 'active';

            return (
              <tr
                key={project.id}
                className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors"
                onClick={() => navigate(`/project/${project.id}`)}
              >
                <td className="px-4 py-3 text-sm">
                  <div className="font-medium text-slate-900 truncate max-w-xs">
                    {getProjectDisplayName(project)}
                  </div>
                  {project.claim_number && (
                    <div className="text-xs text-slate-500">{project.claim_number}</div>
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-slate-600">
                  {displayDate ? formatDate(displayDate, { month: 'short', day: 'numeric', year: 'numeric' }) : '--'}
                </td>
                <td className="px-4 py-3 text-sm">
                  <span className={cn(
                    'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                    status === 'active' ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-600'
                  )}>
                    {status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">
                  <SourceBadge source={projectSource} />
                </td>
                <td className="px-4 py-3 text-sm text-right">
                  <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => onArchive(project.id)}
                      className="rounded p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                      title="Archive project"
                    >
                      <Archive className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => onDelete(project.id)}
                      className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600"
                      title="Delete project"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

// ================================================================= //
//          Dashboard Page Component                                   //
// ================================================================= //

export default function DashboardPage() {
  const {
    projects,
    filteredProjects,
    loading,
    error,
    searchQuery,
    sortField,
    sortOrder,
    sourceFilter,
    setSearchQuery,
    setSortField,
    setSortOrder,
    setSourceFilter,
    fetchProjects,
    createProject,
    archiveProject,
    deleteProject,
  } = useProjectStore();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [viewMode, setViewMode] = useState<'list' | 'cards'>('cards');
  const [confirmAction, setConfirmAction] = useState<{ type: 'archive' | 'delete'; id: string } | null>(null);
  const [formData, setFormData] = useState<NewProjectForm>({
    street: '',
    city: '',
    state: '',
    zip: '',
    owner_name: '',
    claim_number: '',
    date_of_loss: '',
    damage_type: '',
    claim_type: '',
    loss_type: '',
    policy_type: '',
    policy_number: '',
  });

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  /**
   * Handles address selection from the AddressAutocomplete component.
   * Pre-fills the form fields with parsed address data.
   *
   * Args:
   *   address (StructuredAddress): The structured address from Google Places.
   */
  const handleAddressSelect = (address: StructuredAddress) => {
    setFormData((prev) => ({
      ...prev,
      street: address.street || prev.street,
      city: address.city || prev.city,
      state: address.state || prev.state,
      zip: address.zip || prev.zip,
    }));
  };

  const handleCreateProject = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.street.trim() || !formData.city.trim() || !formData.state || !formData.zip.trim()) return;

    setIsCreating(true);
    // Reason: Only send non-empty optional fields to avoid backend validation issues
    const payload: NewProjectForm = {
      street: formData.street,
      city: formData.city,
      state: formData.state,
      zip: formData.zip,
    };
    if (formData.owner_name?.trim()) payload.owner_name = formData.owner_name;
    if (formData.claim_number?.trim()) payload.claim_number = formData.claim_number;
    if (formData.date_of_loss) payload.date_of_loss = formData.date_of_loss;
    if (formData.damage_type) payload.damage_type = formData.damage_type;
    if (formData.claim_type) payload.claim_type = formData.claim_type;
    if (formData.loss_type?.trim()) payload.loss_type = formData.loss_type.trim();
    if (formData.policy_type?.trim()) payload.policy_type = formData.policy_type.trim();
    if (formData.policy_number?.trim()) payload.policy_number = formData.policy_number.trim();

    const newProject = await createProject(payload);
    setIsCreating(false);

    if (newProject) {
      setIsModalOpen(false);
      setFormData({
        street: '', city: '', state: '', zip: '',
        owner_name: '', claim_number: '', date_of_loss: '',
        damage_type: '', claim_type: '', loss_type: '', policy_type: '', policy_number: '',
      });
    }
  };

  /**
   * Handles sort column click. Toggles order if same field, sets asc for new field.
   *
   * Args:
   *   field (ProjectSortField): The field to sort by.
   */
  const handleSort = (field: ProjectSortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  /**
   * Handles the confirmed archive or delete action.
   */
  const handleConfirmAction = async () => {
    if (!confirmAction) return;

    if (confirmAction.type === 'archive') {
      await archiveProject(confirmAction.id);
    } else {
      await deleteProject(confirmAction.id);
    }
    setConfirmAction(null);
  };

  /**
   * Clears all active filters and search.
   */
  const handleClearFilters = () => {
    setSearchQuery('');
    setSourceFilter('all');
    setSortField('date');
    setSortOrder('desc');
  };

  // Reason: Check if any filter is active to show clear-filters option
  const hasActiveFilters = searchQuery !== '' || sourceFilter !== 'all';

  const sourceFilterOptions: { value: ProjectSource | 'all'; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'web', label: 'Web' },
    { value: 'mobile', label: 'Mobile' },
    { value: 'uploaded', label: 'Uploaded' },
  ];

  const renderContent = () => {
    if (loading) {
      return (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-5 w-3/4 rounded bg-slate-200"></div>
                <div className="mt-2 h-4 w-full rounded bg-slate-200"></div>
              </CardHeader>
              <CardContent><div className="h-4 w-5/6 rounded bg-slate-200"></div></CardContent>
            </Card>
          ))}
        </div>
      );
    }

    if (error) {
      return (
        <div className="text-center text-red-500 py-12">
          <p>Error loading projects: {error}</p>
        </div>
      );
    }

    if (projects.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 py-24 text-center bg-white shadow-sm">
          <Folder className="h-16 w-16 text-gray-400" />
          <h3 className="mt-4 text-xl font-semibold text-gray-800">No Projects Yet</h3>
          <p className="mt-2 text-sm text-gray-500">Get started by creating your first project.</p>
          <Button onClick={() => setIsModalOpen(true)} className="mt-6">
            <Plus className="-ml-1 mr-2 h-5 w-5" />
            Create Your First Project
          </Button>
        </div>
      );
    }

    if (filteredProjects.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="text-gray-500">No projects match your filters.</p>
          <Button variant="ghost" onClick={handleClearFilters} className="mt-3">
            Clear filters
          </Button>
        </div>
      );
    }

    if (viewMode === 'cards') {
      return (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {filteredProjects.map((project) => (
            <ProjectCard
              key={project.id}
              project={project}
              onArchive={(id) => setConfirmAction({ type: 'archive', id })}
              onDelete={(id) => setConfirmAction({ type: 'delete', id })}
            />
          ))}
        </div>
      );
    }

    return (
      <ProjectTableView
        projects={filteredProjects}
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={handleSort}
        onArchive={(id) => setConfirmAction({ type: 'archive', id })}
        onDelete={(id) => setConfirmAction({ type: 'delete', id })}
      />
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Project Dashboard</h2>
          <p className="text-muted-foreground">
            {projects.length} project{projects.length !== 1 ? 's' : ''} total
          </p>
        </div>
        <div className="flex items-center gap-3">
          <ViewToggle view={viewMode} onChange={setViewMode} />
          <Button onClick={() => setIsModalOpen(true)}>
            <Plus className="-ml-1 mr-2 h-5 w-5" />
            New Project
          </Button>
        </div>
      </div>

      {/* Search + Sort + Source Filter */}
      {projects.length > 0 && (
        <div className="flex flex-wrap items-center gap-4">
          {/* Search */}
          <div className="relative max-w-md flex-grow">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by owner, address, or claim..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* Sort dropdown */}
          <select
            value={`${sortField}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-') as [ProjectSortField, 'asc' | 'desc'];
              setSortField(field);
              setSortOrder(order);
            }}
            className="rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <option value="date-desc">Newest first</option>
            <option value="date-asc">Oldest first</option>
            <option value="name-asc">Name (A-Z)</option>
            <option value="name-desc">Name (Z-A)</option>
            <option value="status-asc">Status</option>
          </select>

          {/* Source filter tabs */}
          <div className="inline-flex gap-1">
            {sourceFilterOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setSourceFilter(opt.value)}
                className={cn(
                  'px-3 py-1.5 text-sm font-medium rounded-md transition-colors',
                  sourceFilter === opt.value
                    ? 'bg-gray-900 text-white'
                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                )}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {/* Clear filters */}
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={handleClearFilters}>
              Clear filters
            </Button>
          )}
        </div>
      )}

      {/* Project Grid / Table */}
      <div className="rounded-xl bg-slate-50 p-2 md:p-4">
        {renderContent()}
      </div>

      {/* --- New Project Modal --- */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <Card className="w-full max-w-2xl border border-slate-200 shadow-xl bg-white mx-4">
            <form onSubmit={handleCreateProject}>
              <CardHeader>
                <CardTitle>Create New Project</CardTitle>
                <CardDescription>
                  Enter the property details for your new estimation project.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Address Autocomplete */}
                <div className="space-y-2">
                  <Label>Quick Address Lookup</Label>
                  <AddressAutocomplete
                    onSelect={handleAddressSelect}
                    placeholder="Start typing an address to auto-fill..."
                    className="border-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Select an address above to auto-fill, or enter manually below.
                  </p>
                </div>

                {/* Street */}
                <div className="space-y-2">
                  <Label htmlFor="street">Street Address *</Label>
                  <Input
                    id="street"
                    placeholder="123 Main St"
                    value={formData.street}
                    onChange={(e) => setFormData(prev => ({ ...prev, street: e.target.value }))}
                    required
                  />
                </div>

                {/* City / State / ZIP row */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="city">City *</Label>
                    <Input
                      id="city"
                      placeholder="Denver"
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="state">State *</Label>
                    <select
                      id="state"
                      value={formData.state}
                      onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                      required
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="">Select...</option>
                      {US_STATES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="zip">ZIP Code *</Label>
                    <Input
                      id="zip"
                      placeholder="80202"
                      value={formData.zip}
                      onChange={(e) => setFormData(prev => ({ ...prev, zip: e.target.value }))}
                      required
                      maxLength={10}
                    />
                  </div>
                </div>

                {/* Optional fields row */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="ownerName">Owner Name</Label>
                    <Input
                      id="ownerName"
                      placeholder="John Smith"
                      value={formData.owner_name || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, owner_name: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="claimNumber">Claim Number</Label>
                    <Input
                      id="claimNumber"
                      placeholder="CLM-2024-001"
                      value={formData.claim_number || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, claim_number: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="dateOfLoss">Date of Loss</Label>
                    <Input
                      id="dateOfLoss"
                      type="date"
                      value={formData.date_of_loss || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, date_of_loss: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="policyNumber">Policy Number</Label>
                    <Input
                      id="policyNumber"
                      placeholder="POL-2024-001"
                      value={formData.policy_number || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, policy_number: e.target.value }))}
                    />
                  </div>
                </div>

                {/* Damage / Claim / Loss / Policy type — scope-affecting context */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="damageType">Damage Type</Label>
                    <select
                      id="damageType"
                      value={formData.damage_type || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, damage_type: e.target.value }))}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="">Select damage type...</option>
                      {DAMAGE_TYPES.map((d) => (
                        <option key={d.value} value={d.value}>{d.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="claimType">Claim Type</Label>
                    <select
                      id="claimType"
                      value={formData.claim_type || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, claim_type: e.target.value }))}
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    >
                      <option value="">Select claim type...</option>
                      {CLAIM_TYPES.map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="lossType">Loss Type</Label>
                    <Input
                      id="lossType"
                      placeholder="e.g. burst pipe, roof leak"
                      value={formData.loss_type || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, loss_type: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="policyType">Policy Type</Label>
                    <Input
                      id="policyType"
                      placeholder="e.g. HO-3, DP-3"
                      value={formData.policy_type || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, policy_type: e.target.value }))}
                    />
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex justify-end space-x-2">
                <Button variant="ghost" type="button" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" isLoading={isCreating}>
                  Create Project
                </Button>
              </CardFooter>
            </form>
          </Card>
        </div>
      )}

      {/* --- Confirmation Dialog --- */}
      {confirmAction && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <Card className="w-full max-w-sm border border-slate-200 shadow-xl bg-white mx-4">
            <CardHeader>
              <CardTitle className="text-lg">
                {confirmAction.type === 'archive' ? 'Archive Project' : 'Delete Project'}
              </CardTitle>
              <CardDescription>
                {confirmAction.type === 'archive'
                  ? 'This project will be archived and hidden from your dashboard. You can restore it later.'
                  : 'This action cannot be undone. The project and all its data will be permanently deleted.'}
              </CardDescription>
            </CardHeader>
            <CardFooter className="flex justify-end space-x-2">
              <Button variant="ghost" onClick={() => setConfirmAction(null)}>
                Cancel
              </Button>
              <Button
                variant={confirmAction.type === 'delete' ? 'destructive' : 'primary'}
                onClick={handleConfirmAction}
              >
                {confirmAction.type === 'archive' ? 'Archive' : 'Delete'}
              </Button>
            </CardFooter>
          </Card>
        </div>
      )}
    </div>
  );
}
